import React, { useState, useMemo, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollableTabBar from './ScrollableTabBar';
import { 
  XMarkIcon, 
  CheckIcon,
  FolderIcon,
  TrashIcon,
  UserIcon,
  PlayIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import {
  assetGroups,
  assetCategories, 
  getFilteredAssets,
  getFilteredGroups,
  fieldToCategoryMap,
  resolveLibraryUrl,
} from '../data/assetLibrary';
import { GAME_TYPE_PERSPECTIVE, getBackgroundOrientation } from '../data/GameTypes';
import customAssetsService from '../services/customAssetsService';
import customCharacterSetsService from '../services/customCharacterSetsService';
import { isProceduralBackground, getProceduralKey } from '../games/proceduralBackgrounds';
import ProceduralThumbnail from './ProceduralThumbnail';
import { storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useUser } from '../userContext';

/**
 * AssetLibraryPicker - Modal for browsing and selecting pre-made game assets
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when an asset is selected (receives asset object or group)
 * @param {string} field - The field being edited (used to filter by category)
 * @param {string} gameType - Current game type (used to filter compatible assets)
 * @param {string} currentValue - Currently selected asset URL (for highlighting)
 * @param {function} onRequestUpload - When set, My Assets shows an Upload button that triggers this (parent's file input)
 * @param {function} onUploadCustom - When set, modal shows a prominent "Upload your own" button (footer). Parent decides what happens (e.g. open inline upload, expand Advanced, etc.)
 * @param {string} uploadCustomLabel - Optional label for the Upload your own button (default: "Upload your own")
 */
const AssetLibraryPicker = forwardRef(({
  isOpen,
  onClose,
  onSelect,
  field,
  gameType = null,
  currentValue = null,
  allowGroups = true, // Whether to show asset groups (false for single-image fields)
  groupsOnly = false, // When true, only groups are shown/selectable (no individual assets)
  companyId = null, // Company ID for custom assets
  categoryOnly = false, // When true, no category sidebar/tabs  -  only the field's category is shown
  title = null, // Optional custom modal title (e.g. "Platform sets")
  subtitle = null, // Optional custom subtitle under the title
  onRequestUpload = null, // When provided, My Assets category shows an Upload button
  onUploadCustom = null, // When provided, modal shows an "Upload your own" CTA in the footer
  uploadCustomLabel = 'Upload your own',
  allowMultipleSelect = false, // When true, user can select multiple assets; onSelect receives { multiple: true, urls: string[] }
}, ref) => {
  // Subscriptions retired; useUser kept for any future hooks but no longer needed here.
  useUser();
  // Default category: open to the field's mapped category so users see relevant assets first
  const rawCategory = fieldToCategoryMap[field] || 'player';
  const mappedCategory = rawCategory === 'texture' ? 'background' : rawCategory;
  // Only default to My Assets when there's no library category for this field (pure upload fields)
  const preferMyAssets = !!companyId && !groupsOnly && field !== 'playerAnimationFrames' && !fieldToCategoryMap[field];
  const defaultCategory = preferMyAssets ? 'my-assets' : mappedCategory;
  // My Assets tab: show for uploads/frame pickers, not for top-level character select (playerAnimationFrames = choose character set)
  const showMyAssetsCategory = companyId && !groupsOnly && field !== 'playerAnimationFrames';
  
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [hoveredAsset, setHoveredAsset] = useState(null);
  const [hoveredFrameIndex, setHoveredFrameIndex] = useState(0);
  const [customAssets, setCustomAssets] = useState([]);
  const [loadingCustomAssets, setLoadingCustomAssets] = useState(false);
  const [deletingAssetId, setDeletingAssetId] = useState(null);
  const [uploadingMyAssets, setUploadingMyAssets] = useState(false);
  const [multiSelectedUrls, setMultiSelectedUrls] = useState([]);
  const [customCharacterSets, setCustomCharacterSets] = useState([]);
  const [deletingCharacterSetId, setDeletingCharacterSetId] = useState(null);
  const myAssetsInputRef = useRef(null);

  // Whether this picker should show custom character sets (only for character selection)
  const showCustomCharacterSets = field === 'playerAnimationFrames' && companyId && allowGroups;

  // Reset multi-selection when opening in multi mode
  useEffect(() => {
    if (isOpen && allowMultipleSelect) {
      setMultiSelectedUrls([]);
    }
  }, [isOpen, allowMultipleSelect]);
  
  // When modal opens, default to the category for the field (e.g. Players for character, Platforms for platforms)
  useEffect(() => {
    if (isOpen) {
      setSelectedCategory(defaultCategory);
    }
  }, [isOpen, defaultCategory]);

  const fetchCustomAssetsRef = useRef(null);
  fetchCustomAssetsRef.current = async () => {
    if (!companyId) return;
    setLoadingCustomAssets(true);
    try {
      const assets = await customAssetsService.getCompanyAssets(companyId);
      setCustomAssets(assets);
    } catch (error) {
      console.error('Error fetching custom assets:', error);
    } finally {
      setLoadingCustomAssets(false);
    }
  };

  const fetchCustomCharacterSetsRef = useRef(null);
  fetchCustomCharacterSetsRef.current = async () => {
    if (!companyId || !showCustomCharacterSets) return;
    try {
      const sets = await customCharacterSetsService.getCompanyCharacterSets(companyId);
      setCustomCharacterSets(sets);
    } catch (error) {
      console.error('Error fetching custom character sets:', error);
    }
  };

  useEffect(() => {
    if (isOpen && companyId) {
      fetchCustomAssetsRef.current?.();
      fetchCustomCharacterSetsRef.current?.();
    }
  }, [isOpen, companyId]);

  useImperativeHandle(ref, () => ({
    refetchCustomAssets: () => fetchCustomAssetsRef.current?.(),
  }), []);

  const handleDeleteCharacterSet = async (e, setId) => {
    e.stopPropagation();
    if (!companyId) return;
    setDeletingCharacterSetId(setId);
    try {
      await customCharacterSetsService.deleteCharacterSet(companyId, setId);
      setCustomCharacterSets(prev => prev.filter(s => s.id !== setId));
    } catch (error) {
      console.error('Error deleting character set:', error);
    } finally {
      setDeletingCharacterSetId(null);
    }
  };
  
  // Filter out audio (has its own SoundSelect), texture (removed), and platform unless this picker is for a platform field
  const imageCategories = assetCategories.filter(cat => {
    if (cat.id === 'audio') return false;
    if (cat.id === 'texture') return false;
    if (cat.id === 'platform' && defaultCategory !== 'platform') return false;
    if (cat.id === 'pipe' && defaultCategory !== 'pipe') return false;
    return true;
  });
  
  // Add "My Assets" at top for frame uploads etc.; hidden for top-level character select (characters only)
  const allCategories = useMemo(() => {
    const withMyAssets = showMyAssetsCategory
      ? [{ id: 'my-assets', name: 'My Assets', icon: <UserIcon className="w-5 h-5" /> }, ...imageCategories]
      : imageCategories;
    return withMyAssets;
  }, [showMyAssetsCategory, imageCategories]);
  
  // Handle delete custom asset
  const handleDeleteAsset = async (e, assetId, assetUrl) => {
    e.stopPropagation();
    if (!companyId) return;
    
    setDeletingAssetId(assetId);
    try {
      await customAssetsService.deleteAsset(companyId, assetId, true, assetUrl);
      setCustomAssets(prev => prev.filter(a => a.id !== assetId));
    } catch (error) {
      console.error('Error deleting asset:', error);
    } finally {
      setDeletingAssetId(null);
    }
  };

  // Multi-file upload to My Assets: upload each file to storage, save to custom assets, refetch
  const handleMyAssetsFilesSelected = async (e) => {
    const files = e.target.files;
    if (!files?.length || !companyId) {
      e.target.value = '';
      return;
    }
    const maxAssets = customAssetsService.getMaxAssets();
    const imageTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp', 'image/gif'];
    const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
    const imageFiles = Array.from(files).filter(f => imageTypes.includes(f.type) && f.size <= MAX_FILE_SIZE);
    if (imageFiles.length < Array.from(files).filter(f => imageTypes.includes(f.type)).length) {
      console.warn('Some files were skipped (over 3MB limit)');
    }
    const remaining = Math.max(0, maxAssets - customAssets.length);
    const toUpload = imageFiles.slice(0, remaining);
    if (toUpload.length === 0) {
      e.target.value = '';
      return;
    }
    setUploadingMyAssets(true);
    const category = customAssetsService.getFieldCategory(field);
    let limitReached = false;
    try {
      for (const file of toUpload) {
        if (limitReached) break;
        const safeName = (file.name || 'image').replace(/[/\\]/g, '_');
        const path = `workspace/${companyId}/customAssets/${Date.now()}_${safeName}`;
        const storageReference = storageRef(storage, path);
        await new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageReference, file);
          uploadTask.on(
            'state_changed',
            () => {},
            reject,
            async () => {
              try {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                const result = await customAssetsService.saveAsset(companyId, {
                  url,
                  name: file.name || `Custom ${category}`,
                  category,
                  field,
                });
                if (result?.error === 'limit_reached') limitReached = true;
                if (result?.id) {
                  await fetchCustomAssetsRef.current?.();
                }
              } catch (err) {
                console.warn('Save to My Assets failed:', err);
              }
              resolve();
            }
          );
        });
      }
    } catch (error) {
      console.error('Error uploading to My Assets:', error);
    } finally {
      setUploadingMyAssets(false);
      e.target.value = '';
    }
  };

  // Filter assets by category. For the player category we also apply a
  // perspective filter so the picker only shows characters that match the
  // game type's needs (side-view for sidescroller/flappy/verticalclimber,
  // back-view for runner). Other categories ignore game type.
  const GAME_BACKGROUND_FIELDS = ['backgroundImage', 'gameBackgroundImage', 'canvasBackgroundImage'];
  const isGameBackgroundField = GAME_BACKGROUND_FIELDS.includes(field);
  const requiredPerspective = gameType ? GAME_TYPE_PERSPECTIVE[gameType] : null;
  const allowedPerspectives = useMemo(() => {
    if (!requiredPerspective) return null;
    return Array.isArray(requiredPerspective) ? requiredPerspective : [requiredPerspective];
  }, [requiredPerspective]);
  const matchesPerspective = (perspective) => {
    if (!allowedPerspectives) return true;
    if (!perspective) return true;
    // Asset perspective can be a single string or an array (e.g. Red Spaceship
    // works as both 'behind' and 'front'). Match if any overlap with allowed.
    const assetList = Array.isArray(perspective) ? perspective : [perspective];
    return assetList.some(p => allowedPerspectives.includes(p));
  };
  // Orientation filter: vertical games (Lane Dash, Sky Bounce) only see bgs
  // tagged 'vertical' or untagged (universal). Horizontal games (sidescroller,
  // flappy) only see 'horizontal' or untagged. Games without a scroll direction
  // see everything.
  const bgOrientation = gameType ? getBackgroundOrientation(gameType) : null;
  const matchesBgOrientation = (asset) => {
    if (!bgOrientation) return true;
    if (!asset.orientation) return true; // untagged = universal
    return asset.orientation === bgOrientation;
  };

  const filteredAssets = useMemo(() => {
    if (selectedCategory === 'my-assets') {
      return []; // Custom assets are shown separately
    }
    let assets = getFilteredAssets({ category: selectedCategory });
    if (selectedCategory === 'player' && allowedPerspectives) {
      assets = assets.filter(a => matchesPerspective(a.perspective));
    }
    // Only show procedural animated backgrounds when the picker is opened for a game background field
    if (!isGameBackgroundField) {
      return assets.filter(a => !isProceduralBackground(a.url));
    }
    // For background fields, hide bgs whose orientation doesn't match the
    // game's scroll direction.
    assets = assets.filter(matchesBgOrientation);
    // Group: animated (procedural) first, then static.
    return [
      ...assets.filter(a => isProceduralBackground(a.url)),
      ...assets.filter(a => !isProceduralBackground(a.url)),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, isGameBackgroundField, allowedPerspectives, bgOrientation]);

  const filteredGroups = useMemo(() => {
    if (selectedCategory === 'my-assets') {
      return []; // No groups for custom assets
    }
    let groups = getFilteredGroups({ category: selectedCategory });
    if (selectedCategory === 'player' && allowedPerspectives) {
      groups = groups.filter(g => matchesPerspective(g.perspective));
    }
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, allowedPerspectives]);

  // Cycle animation frames when hovering over a character group (preset or custom)
  const hoveredGroupFrames = useMemo(() => {
    if (!hoveredAsset || !allowGroups) return null;
    const group = filteredGroups.find(g => g.id === hoveredAsset);
    if (group) return group?.assets?.playerAnimationFrames || null;
    // Check custom character sets
    const customSet = customCharacterSets.find(s => s.id === hoveredAsset);
    return customSet?.playerAnimationFrames || null;
  }, [hoveredAsset, allowGroups, filteredGroups, customCharacterSets]);

  useEffect(() => {
    if (!hoveredAsset) {
      setHoveredFrameIndex(0);
      return;
    }
    if (!hoveredGroupFrames || hoveredGroupFrames.length <= 1) return;
    setHoveredFrameIndex(0);
    const interval = setInterval(() => {
      setHoveredFrameIndex(i => (i + 1) % hoveredGroupFrames.length);
    }, 200);
    return () => clearInterval(interval);
  }, [hoveredAsset, hoveredGroupFrames]);

  // Get URLs from a group (for multi-select: character frames or single image)
  const getGroupUrls = (group) => {
    if (group.assets?.playerAnimationFrames?.length) return group.assets.playerAnimationFrames;
    if (group.assets?.playerImage) return [group.assets.playerImage];
    const urls = Object.values(group.assets || {}).filter((v) => typeof v === 'string');
    return urls.length ? urls : [];
  };

  // Handle asset selection
  const handleSelect = (asset) => {
    if (allowMultipleSelect) {
      setMultiSelectedUrls((prev) =>
        prev.includes(asset.url) ? prev.filter((u) => u !== asset.url) : [...prev, asset.url]
      );
      return;
    }
    onSelect(asset);
    onClose();
  };

  // Handle group selection - passes the group with isGroup flag, or in multi mode adds group URLs to selection
  const handleGroupSelect = (group) => {
    if (allowMultipleSelect) {
      const urls = getGroupUrls(group);
      if (urls.length === 0) return;
      setMultiSelectedUrls((prev) => {
        const next = new Set(prev);
        urls.forEach((u) => next.add(u));
        return [...next];
      });
      return;
    }
    onSelect({ ...group, isGroup: true });
    onClose();
  };

  const handleConfirmMultiSelect = () => {
    if (multiSelectedUrls.length === 0) return;
    onSelect({ multiple: true, urls: multiSelectedUrls });
    setMultiSelectedUrls([]);
    onClose();
  };

  const removeFromMultiSelect = (urlToRemove) => {
    setMultiSelectedUrls((prev) => prev.filter((u) => u !== urlToRemove));
  };

  const logForRemoval = (item, type = 'asset') => {
    if (type === 'group') {
      console.log(`ASSET_REMOVE_GROUP: "${item.id}"  // ${item.name || item.id}`);
    } else {
      console.log(`ASSET_REMOVE: "${item.id}"  // ${item.name || item.id} (${item.category || '?'})`);
    }
  };

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - full viewport, no gap */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-2 sm:inset-4 md:inset-8 lg:inset-16 gch-bg-card rounded-2xl shadow-2xl z-[51] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--admin-border)] bg-white/5">
              <div>
                <h2 className="text-xl font-bold gch-text-primary" style={{ fontFamily: '"Lexend", sans-serif' }}>
                  {allowMultipleSelect ? (title ?? 'Select multiple frames') : (title ?? 'Browse assets')}
                </h2>
                <p className="text-sm gch-text-secondary mt-0.5">
                  {allowMultipleSelect
                    ? 'Click assets or character sets to add them. Then click "Add frames" below.'
                    : (subtitle ?? 'Choose from our collection of pre-made game assets')}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg gch-text-secondary hover:gch-text-primary hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Mobile Category Tabs - hidden when categoryOnly */}
            {!categoryOnly && (
            <ScrollableTabBar fadeFrom="var(--admin-bg-secondary)" mobileOnly={false} className="sm:hidden border-b border-[var(--admin-border)] gch-input-ghost flex-shrink-0" innerClassName="px-4 py-3">
              <div className="flex gap-2">
                {allCategories.map(category => {
                  const count = category.id === 'my-assets'
                    ? customAssets.length
                    : getFilteredAssets({ category: category.id }).length;
                  
                  return (
                    <button
                      type="button"
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                        selectedCategory === category.id
                          ? category.id === 'my-assets'
                            ? 'bg-[var(--admin-accent-light)] text-[var(--admin-accent)] font-semibold'
                            : 'bg-[var(--admin-accent-light)] text-[var(--admin-accent)] font-semibold'
                          : 'gch-text-secondary bg-white/5'
                      }`}
                    >
                      <span>{typeof category.icon === 'string' ? category.icon : category.icon}</span>
                      <span>{category.name}</span>
                      {category.id === 'my-assets' && count > 0 && (
                        <span className="text-xs bg-[var(--admin-accent-light)] text-[var(--admin-accent)] px-1.5 py-0.5 rounded-full">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </ScrollableTabBar>
            )}

            {/* Multi-select: show selected frames to add */}
            {allowMultipleSelect && multiSelectedUrls.length > 0 && (
              <div className="flex-shrink-0 px-4 py-3 border-b border-[var(--admin-border)] bg-[var(--admin-accent)]/5">
                <p className="text-xs font-semibold gch-text-primary mb-2">
                  Selected to add ({multiSelectedUrls.length})
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1 min-h-[56px]">
                  {multiSelectedUrls.map((url) => (
                    <div
                      key={url}
                      className="relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 border-[var(--admin-accent)] bg-white/10"
                    >
                      <img
                        src={resolveLibraryUrl(url)}
                        alt=""
                        className="w-full h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => removeFromMultiSelect(url)}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity text-white"
                        title="Remove from selection"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-1 overflow-hidden min-h-0">
              {/* Category Sidebar - Desktop only; hidden when categoryOnly */}
              {!categoryOnly && (
              <div className="w-56 flex-shrink-0 border-r border-[var(--admin-border)] bg-white/5 p-3 overflow-y-auto hidden sm:block">
                <div className="space-y-1">
                  {allCategories.map(category => {
                    let count;
                    if (category.id === 'my-assets') {
                      count = customAssets.length;
                    } else {
                      const assets = getFilteredAssets({ category: category.id });
                      const groups = getFilteredGroups({ category: category.id });
                      count = assets.length + groups.length;
                    }
                    
                    const isMyAssets = category.id === 'my-assets';
                    
                    return (
                      <button
                        type="button"
                        key={category.id}
                        onClick={() => handleCategoryChange(category.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                          selectedCategory === category.id
                            ? isMyAssets
                              ? 'bg-[var(--admin-accent-light)] text-[var(--admin-accent)] font-semibold'
                              : 'bg-[var(--admin-accent-light)] text-[var(--admin-accent)] font-semibold'
                            : 'gch-text-secondary hover:bg-white/5 hover:gch-text-primary'
                        }`}
                      >
                        <span className="text-lg">
                          {typeof category.icon === 'string' ? category.icon : category.icon}
                        </span>
                        <span className="flex-1 text-sm">{category.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          selectedCategory === category.id
                            ? isMyAssets
                              ? 'bg-[var(--admin-accent-light)] text-[var(--admin-accent)]'
                              : 'bg-[var(--admin-accent-light)] text-[var(--admin-accent)]'
                            : 'bg-white/10 gch-text-secondary'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Asset Grid */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {/* My Assets Section */}
                {selectedCategory === 'my-assets' ? (
                  loadingCustomAssets ? (
                    <div className="flex flex-col items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--admin-accent)] mb-3"></div>
                      <p className="text-sm font-medium gch-text-primary">Loading your assets...</p>
                    </div>
                  ) : (
                    <div>
                      {customAssets.length >= 400 && (
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                          <p className="text-sm gch-text-secondary">
                            {customAssets.length} of {customAssetsService.getMaxAssets()} assets used
                          </p>
                        </div>
                      )}
                      <motion.div
                        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4"
                        variants={{
                          visible: {
                            transition: {
                              staggerChildren: 0.04,
                              delayChildren: 0.02,
                            },
                          },
                          hidden: {},
                        }}
                        initial="hidden"
                        animate="visible"
                        key="my-assets"
                      >
                        {(onRequestUpload || companyId) && (
                          <>
                            <input
                              ref={myAssetsInputRef}
                              type="file"
                              multiple
                              accept="image/jpeg,image/png,image/svg+xml,image/webp,image/gif"
                              className="sr-only"
                              aria-label="Upload images to My Assets"
                              onChange={handleMyAssetsFilesSelected}
                            />
                            <motion.button
                              type="button"
                              disabled={uploadingMyAssets || customAssets.length >= customAssetsService.getMaxAssets()}
                              onClick={() => myAssetsInputRef.current?.click()}
                              variants={{
                                hidden: { opacity: 0, y: 6 },
                                visible: { opacity: 1, y: 0 },
                              }}
                              transition={{ duration: 0.2 }}
                              whileHover={!uploadingMyAssets ? { scale: 1.02 } : undefined}
                              whileTap={!uploadingMyAssets ? { scale: 0.98 } : undefined}
                              className="relative rounded-xl overflow-hidden border-2 border-dashed border-[var(--admin-accent)]/50 hover:border-[var(--admin-accent)] bg-[var(--admin-accent-light)]/20 hover:bg-[var(--admin-accent-light)]/30 transition-colors flex flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--admin-bg-surface)] disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              <div className="aspect-square bg-white/5 flex flex-col items-center justify-center p-4 gap-1">
                                <ArrowUpTrayIcon className="w-10 h-10 text-[var(--admin-accent)] flex-shrink-0" />
                                <span className="text-[10px] sm:text-xs font-medium gch-text-secondary text-center">
                                  {uploadingMyAssets ? 'Uploading…' : 'Upload'}
                                </span>
                              </div>
                            </motion.button>
                          </>
                        )}
                        {customAssets.map(asset => {
                          const isSelected = currentValue === asset.url;
                          const isHovered = hoveredAsset === asset.id;
                          const isDeleting = deletingAssetId === asset.id;
                          
                          return (
                            <motion.div
                              key={asset.id}
                              className="contents"
                              variants={{
                                hidden: { opacity: 0, y: 6 },
                                visible: { opacity: 1, y: 0 },
                              }}
                              transition={{ duration: 0.2 }}
                            >
                            <motion.button
                              type="button"
                              key={asset.id}
                              onClick={(e) => {
                                if (e.shiftKey) {
                                  e.preventDefault();
                                  logForRemoval(asset);
                                  return;
                                }
                                handleSelect(asset);
                              }}
                              onMouseEnter={() => setHoveredAsset(asset.id)}
                              onMouseLeave={() => setHoveredAsset(null)}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              disabled={isDeleting}
                              className={`relative group rounded-xl overflow-hidden border-2 transition-all flex flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--admin-bg-surface)] ${
                                isSelected
                                  ? 'border-[var(--admin-accent)] ring-2 ring-[var(--admin-accent)]/30'
                                  : 'border-[var(--admin-border)] hover:border-[var(--admin-accent)]'
                              } ${isDeleting ? 'opacity-50' : ''}`}
                            >
                              {/* Asset Preview  -  backgrounds cover the cell for a proper preview */}
                              <div className="aspect-square bg-white/5 p-3 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                <img
                                  src={asset.url}
                                  alt={asset.name}
                                  className={`max-w-full max-h-full ${asset.category === 'background' ? 'object-cover w-full h-full' : 'object-contain'}`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                              {/* File name */}
                              <div className="px-2 py-1.5 min-h-0 flex-shrink-0 bg-white/5 border-t border-[var(--admin-border)]">
                                <span
                                  className="block text-[10px] sm:text-xs gch-text-secondary truncate font-medium"
                                  title={asset.name || 'Asset'}
                                >
                                  {asset.name || 'Asset'}
                                </span>
                              </div>

                              {/* Selected Checkmark */}
                              {isSelected && (
                                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--admin-accent)] flex items-center justify-center shadow-lg">
                                  <CheckIcon className="w-4 h-4 text-white" />
                                </div>
                              )}

                              {/* Remove button - visible on hover */}
                              <button
                                type="button"
                                onClick={(e) => handleDeleteAsset(e, asset.id, asset.url)}
                                disabled={isDeleting}
                                className="absolute top-2 left-2 p-1.5 rounded-lg gch-input-ghost gch-text-muted hover:gch-text-primary hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-colors shadow-sm"
                                title="Remove asset"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>

                              {/* Hover Overlay */}
                              <AnimatePresence>
                                {isHovered && !isSelected && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 bg-[var(--admin-accent)]/10 flex items-center justify-center pointer-events-none"
                                  >
                                    <span className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm shadow-lg text-xs font-semibold text-[var(--admin-accent)]">
                                      Select
                                    </span>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.button>
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    </div>
                  )
                ) : ((allowGroups && (filteredGroups.length > 0 || customCharacterSets.length > 0)) || (!groupsOnly && filteredAssets.length > 0)) ? (
                  <div className="space-y-6">
                    {/* Custom Character Sets - shown above preset groups when browsing characters */}
                    {showCustomCharacterSets && customCharacterSets.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold gch-text-primary mb-3">My Characters</h3>
                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
                          variants={{
                            visible: { transition: { staggerChildren: 0.04, delayChildren: 0.02 } },
                            hidden: {},
                          }}
                          initial="hidden"
                          animate="visible"
                          key="custom-character-sets"
                        >
                          {customCharacterSets.map(set => {
                            const isHovered = hoveredAsset === set.id;
                            const frames = set.playerAnimationFrames || [];
                            const isDeleting = deletingCharacterSetId === set.id;
                            const groupUrls = frames.length ? frames : [];
                            const isGroupSelected = allowMultipleSelect && groupUrls.some(u => multiSelectedUrls.includes(u));

                            return (
                              <motion.div
                                key={set.id}
                                className="contents"
                                variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0 } }}
                                transition={{ duration: 0.2 }}
                              >
                                <motion.button
                                  type="button"
                                  onClick={() => {
                                    if (allowMultipleSelect) {
                                      if (groupUrls.length === 0) return;
                                      setMultiSelectedUrls(prev => {
                                        const next = new Set(prev);
                                        groupUrls.forEach(u => next.add(u));
                                        return [...next];
                                      });
                                      return;
                                    }
                                    // Convert to the same shape as preset groups
                                    onSelect({
                                      isGroup: true,
                                      name: set.name,
                                      assets: {
                                        playerAnimationFrames: set.playerAnimationFrames,
                                        playerJumpImage: set.playerJumpImage || null,
                                        playerImage: set.playerImage || null,
                                      },
                                    });
                                    onClose();
                                  }}
                                  onMouseEnter={() => setHoveredAsset(set.id)}
                                  onMouseLeave={() => setHoveredAsset(null)}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  disabled={isDeleting}
                                  className={`relative group rounded-xl overflow-hidden border-2 bg-gradient-to-br from-[var(--admin-accent-light)] to-gch-input-ghost transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--admin-bg-surface)] ${
                                    isGroupSelected ? 'border-[var(--admin-accent)] ring-2 ring-[var(--admin-accent)]/30' : 'border-[var(--admin-accent)]/40 hover:border-[var(--admin-accent)]'
                                  } ${isDeleting ? 'opacity-50' : ''}`}
                                >
                                  <div className="p-2">
                                    <div className="flex justify-center items-center h-28">
                                      <div className="flex items-center h-24">
                                        {frames.length > 0 && (
                                          <img
                                            src={
                                              isHovered && frames.length > 1
                                                ? frames[hoveredFrameIndex % frames.length]
                                                : frames[0]
                                            }
                                            alt={set.name}
                                            className="h-full w-auto object-contain"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Animated indicator */}
                                  {frames.length > 1 && !isGroupSelected && (
                                    <div className="absolute top-2 right-2" title="Animated">
                                      <motion.span
                                        animate={{ scale: [1, 1.15, 1] }}
                                        transition={{ repeat: Infinity, duration: 0.6 }}
                                        className="text-[var(--admin-accent)]"
                                      >
                                        <PlayIcon className="w-3.5 h-3.5" />
                                      </motion.span>
                                    </div>
                                  )}
                                  {isGroupSelected && (
                                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--admin-accent)] flex items-center justify-center shadow-lg">
                                      <CheckIcon className="w-4 h-4 text-white" />
                                    </div>
                                  )}

                                  {/* Remove button */}
                                  <button
                                    type="button"
                                    onClick={(e) => handleDeleteCharacterSet(e, set.id)}
                                    disabled={isDeleting}
                                    className="absolute top-2 left-2 p-1.5 rounded-lg gch-input-ghost gch-text-muted hover:gch-text-primary hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-colors shadow-sm"
                                    title="Remove character set"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>

                                  {/* Name label */}
                                  <div className="px-2 py-1.5 min-h-0 flex-shrink-0 bg-white/5 border-t border-[var(--admin-border)]">
                                    <span className="block text-[10px] sm:text-xs gch-text-secondary truncate font-medium" title={set.name}>
                                      {set.name}
                                    </span>
                                  </div>
                                </motion.button>
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      </div>
                    )}

                    {/* Asset Groups Section - only show if groups are allowed */}
                    {allowGroups && filteredGroups.length > 0 && (
                      <div>
                        <motion.div
                          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
                          variants={{
                            visible: {
                              transition: {
                                staggerChildren: 0.04,
                                delayChildren: 0.02,
                              },
                            },
                            hidden: {},
                          }}
                          initial="hidden"
                          animate="visible"
                          key={`${selectedCategory}-groups`}
                        >
                          {filteredGroups.map(group => {
                            const isHovered = hoveredAsset === group.id;
                            const groupUrls = getGroupUrls(group);
                            const isGroupSelected = allowMultipleSelect && groupUrls.some((u) => multiSelectedUrls.includes(u));

                            return (
                              <motion.div
                                key={group.id}
                                className="contents"
                                variants={{
                                  hidden: { opacity: 0, y: 6 },
                                  visible: { opacity: 1, y: 0 },
                                }}
                                transition={{ duration: 0.2 }}
                              >
                              <motion.button
                                type="button"
                                onClick={(e) => {
                                  if (e.shiftKey) {
                                    e.preventDefault();
                                    logForRemoval(group, 'group');
                                    return;
                                  }
                                  handleGroupSelect(group);
                                }}
                                onMouseEnter={() => setHoveredAsset(group.id)}
                                onMouseLeave={() => setHoveredAsset(null)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`relative group rounded-xl overflow-hidden border-2 bg-gradient-to-br from-[var(--admin-accent-light)] to-gch-input-ghost transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--admin-bg-surface)] ${
                                  isGroupSelected ? 'border-[var(--admin-accent)] ring-2 ring-[var(--admin-accent)]/30' : 'border-[var(--admin-accent)]/40 hover:border-[var(--admin-accent)]'
                                }`}
                              >
                                {/* Group Preview - character (taller box) or platform 3-slice */}
                                <div className="p-2">
                                  <div className={`flex justify-center items-center ${group.assets.playerAnimationFrames?.length > 0 ? 'h-28' : 'h-14'}`}>
                                    <div className={`flex items-center ${group.assets.playerAnimationFrames?.length > 0 ? 'h-24' : 'h-12'}`}>
                                      {group.assets.playerAnimationFrames?.length > 0 ? (
                                        <img
                                          src={resolveLibraryUrl(
                                            isHovered && group.assets.playerAnimationFrames.length > 1
                                              ? group.assets.playerAnimationFrames[hoveredFrameIndex % group.assets.playerAnimationFrames.length]
                                              : group.assets.playerAnimationFrames[0]
                                          )}
                                          alt={group.name}
                                          className="h-full w-auto object-contain"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : group.assets.platformImageLeft ? (
                                        <>
                                          <img
                                            src={resolveLibraryUrl(group.assets.platformImageLeft)}
                                            alt={`${group.name} left`}
                                            className="h-full w-auto object-contain"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                                          {group.assets.platformImageMiddle && (
                                            <img
                                              src={resolveLibraryUrl(group.assets.platformImageMiddle)}
                                              alt={`${group.name} middle`}
                                              className="h-full w-auto object-contain"
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                          )}
                                          {group.assets.platformImageRight && (
                                            <img
                                              src={resolveLibraryUrl(group.assets.platformImageRight)}
                                              alt={`${group.name} right`}
                                              className="h-full w-auto object-contain"
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                          )}
                                        </>
                                      ) : group.assets.pipeImageEnd ? (
                                        <img
                                          src={resolveLibraryUrl(group.assets.pipeImageEnd)}
                                          alt={group.name}
                                          className="h-full w-auto object-contain"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        Object.values(group.assets).filter(v => typeof v === 'string').slice(0, 3).map((url, idx) => (
                                          <img
                                            key={idx}
                                            src={resolveLibraryUrl(url)}
                                            alt={`${group.name} ${idx}`}
                                            className="h-full w-auto object-contain"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                                        ))
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Animated indicator - only when multiple frames */}
                                {group.assets.playerAnimationFrames?.length > 1 && !isGroupSelected && (
                                  <div className="absolute top-2 right-2" title="Animated">
                                    <motion.span
                                      animate={{ scale: [1, 1.15, 1] }}
                                      transition={{ repeat: Infinity, duration: 0.6 }}
                                      className="text-[var(--admin-accent)]"
                                    >
                                      <PlayIcon className="w-3.5 h-3.5" />
                                    </motion.span>
                                  </div>
                                )}
                                {/* Selected checkmark (multi-select mode) */}
                                {isGroupSelected && (
                                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--admin-accent)] flex items-center justify-center shadow-lg">
                                    <CheckIcon className="w-4 h-4 text-white" />
                                  </div>
                                )}

                                {/* Hover overlay: skip for character sets so animation is visible */}
                                <AnimatePresence>
                                  {isHovered && !group.assets.playerAnimationFrames?.length && (
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      className="absolute inset-0 bg-[var(--admin-accent)]/10 flex items-center justify-center pointer-events-none"
                                    >
                                      <span className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm shadow-lg text-xs font-semibold text-[var(--admin-accent)]">
                                        Apply Set
                                      </span>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.button>
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      </div>
                    )}

                    {/* Individual Assets Section - hidden when groupsOnly */}
                    {!groupsOnly && filteredAssets.length > 0 && (
                      <div>
                        {allowGroups && filteredGroups.length > 0 && (
                          <h3 className="text-sm font-semibold gch-text-primary mb-3">Individual Assets</h3>
                        )}
                        <motion.div
                          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4"
                          variants={{
                            visible: {
                              transition: {
                                staggerChildren: 0.04,
                                delayChildren: 0.02,
                              },
                            },
                            hidden: {},
                          }}
                          initial="hidden"
                          animate="visible"
                          key={`${selectedCategory}-assets`}
                        >
                          {filteredAssets.map((asset, idx) => {
                            const isSelected = allowMultipleSelect
                              ? multiSelectedUrls.includes(asset.url)
                              : currentValue === asset.url;
                            const isHovered = hoveredAsset === asset.id;
                            // For game-background pickers, insert section
                            // headers between Animated and Static groups.
                            const showAnimatedHeader = selectedCategory === 'background' && isGameBackgroundField && idx === 0 && isProceduralBackground(asset.url);
                            const showStaticHeader = selectedCategory === 'background' && isGameBackgroundField && !isProceduralBackground(asset.url) && (idx === 0 || isProceduralBackground(filteredAssets[idx - 1]?.url));

                            return (
                              <React.Fragment key={asset.id}>
                              {showAnimatedHeader && (
                                <div className="col-span-full -mb-1 mt-0.5 text-[10px] font-semibold uppercase tracking-widest gch-text-muted">Animated</div>
                              )}
                              {showStaticHeader && (
                                <div className="col-span-full -mb-1 mt-2 text-[10px] font-semibold uppercase tracking-widest gch-text-muted">Static</div>
                              )}
                              <motion.div
                                className="contents"
                                variants={{
                                  hidden: { opacity: 0, y: 6 },
                                  visible: { opacity: 1, y: 0 },
                                }}
                                transition={{ duration: 0.2 }}
                              >
                              <motion.button
                                type="button"
                                onClick={(e) => {
                                  if (e.shiftKey) {
                                    e.preventDefault();
                                    logForRemoval(asset);
                                    return;
                                  }
                                  handleSelect(asset);
                                }}
                                onMouseEnter={() => setHoveredAsset(asset.id)}
                                onMouseLeave={() => setHoveredAsset(null)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
className={`relative group rounded-xl overflow-hidden border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--admin-bg-surface)] ${
                                    isSelected
                                      ? 'border-[var(--admin-accent)] ring-2 ring-[var(--admin-accent)]/30'
                                      : 'border-[var(--admin-border)] hover:border-[var(--admin-accent)]'
                                }`}
                              >
                                {/* Asset Preview  -  backgrounds cover the cell for a proper preview */}
                                <div className={`aspect-square bg-white/5 flex items-center justify-center overflow-hidden ${isProceduralBackground(asset.url) ? '' : 'p-3'}`}>
                                  {isProceduralBackground(asset.url) ? (
                                    <ProceduralThumbnail
                                      rendererKey={getProceduralKey(asset.url)}
                                      isStatic
                                    />
                                  ) : (
                                  <img
                                    src={resolveLibraryUrl(asset.thumbnail || asset.url)}
                                    alt={asset.name}
                                    className={`max-w-full max-h-full ${selectedCategory === 'background' ? 'object-cover w-full h-full' : 'object-contain'}`}
                                    onError={(e) => {
                                      // Show placeholder on error
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `
                                        <div class="flex flex-col items-center justify-center gch-text-muted">
                                          <svg class="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                          </svg>
                                          <span class="text-xs">Preview</span>
                                        </div>
                                      `;
                                    }}
                                  />
                                  )}
                                </div>
                                
                                {/* Selected Checkmark */}
                                {isSelected && (
                                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--admin-accent)] flex items-center justify-center shadow-lg">
                                    <CheckIcon className="w-4 h-4 text-white" />
                                  </div>
                                )}

                                {/* Hover Overlay */}
                                <AnimatePresence>
                                  {isHovered && !isSelected && (
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      className="absolute inset-0 bg-[var(--admin-accent)]/10 flex items-center justify-center"
                                    >
                                      <span className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm shadow-lg text-xs font-semibold text-[var(--admin-accent)]">
                                        Select
                                      </span>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.button>
                              </motion.div>
                              </React.Fragment>
                            );
                          })}
                        </motion.div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 gch-text-muted">
                    <FolderIcon className="w-12 h-12 mb-3" />
                    <p className="text-sm font-medium">{groupsOnly ? 'No sets found' : 'No assets found'}</p>
                    {onUploadCustom ? (
                      <button
                        type="button"
                        onClick={() => onUploadCustom()}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--admin-accent-light)] text-[var(--admin-accent)] hover:bg-[var(--admin-accent-light)]/80 transition-colors"
                      >
                        <ArrowUpTrayIcon className="w-4 h-4" />
                        {uploadCustomLabel}
                      </button>
                    ) : (
                      <p className="text-xs mt-1">Try adjusting your filters</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-[var(--admin-border)] bg-white/5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm gch-text-secondary">
                  {allowMultipleSelect ? (
                    <span>{multiSelectedUrls.length} frame{multiSelectedUrls.length !== 1 ? 's' : ''} selected</span>
                  ) : selectedCategory === 'my-assets' ? (
                    `${customAssets.length} of ${customAssetsService.getMaxAssets()} custom assets`
                  ) : groupsOnly ? (
                    allowGroups && filteredGroups.length > 0 ? `${filteredGroups.length} set${filteredGroups.length !== 1 ? 's' : ''} available` : 'No sets found'
                  ) : (
                    <>
                      {allowGroups && filteredGroups.length > 0 && `${filteredGroups.length} set${filteredGroups.length !== 1 ? 's' : ''}, `}
                      {filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''} available
                    </>
                  )}
                </p>
                {selectedCategory !== 'my-assets' && !allowMultipleSelect && null}
              </div>
              <div className="flex items-center gap-2">
                {onUploadCustom && !allowMultipleSelect && (
                  <button
                    type="button"
                    onClick={() => { onUploadCustom(); }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[var(--admin-accent-light)] text-[var(--admin-accent)] hover:bg-[var(--admin-accent-light)]/80 transition-colors"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    {uploadCustomLabel}
                  </button>
                )}
                {allowMultipleSelect && multiSelectedUrls.length > 0 && (
                  <button
                    type="button"
                    onClick={handleConfirmMultiSelect}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-[var(--admin-accent)] text-white hover:opacity-90 transition-colors"
                  >
                    Add {multiSelectedUrls.length} frame{multiSelectedUrls.length !== 1 ? 's' : ''}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg text-sm font-semibold gch-text-secondary hover:gch-text-primary hover:bg-white/10 transition-colors"
                >
                  {allowMultipleSelect && multiSelectedUrls.length > 0 ? 'Cancel' : 'Close'}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
});

AssetLibraryPicker.displayName = 'AssetLibraryPicker';

export default AssetLibraryPicker;
